// Page tools: what the desk agent can see and do on the user's screen. The page on screen registers
// its tools (the stock page: read the chart, switch it, mark a plan); the agent's calls run here, on
// the same engine that draws the chart, and the result goes back to the agent.
import { useEffect, useRef } from 'react';

export type ToolArgs = Record<string, unknown>;
export type PageTool = (args: ToolArgs) => Promise<Record<string, unknown>> | Record<string, unknown>;
type Tools = Record<string, PageTool>;

const layers: { id: number; tools: Tools; fallback: boolean }[] = [];
const listeners = new Set<() => void>();
let nextId = 1;
const changed = () => { for (const l of listeners) l(); };

/** Register this page's tools while it is mounted (the latest mounted page wins per tool). The
 * functions may change every render: the latest ones are always used. `fallback` tools (the desk's
 * own, for pages without one) are used only when no page has that tool, whatever the mount order. */
export function usePageTools(tools: Tools, opts: { fallback?: boolean } = {}) {
  const fallback = !!opts.fallback;
  const ref = useRef(tools);
  ref.current = tools;
  const names = Object.keys(tools).sort().join(',');
  useEffect(() => {
    const id = nextId++;
    const proxy: Tools = {};
    for (const n of names.split(',').filter(Boolean)) proxy[n] = (a) => ref.current[n](a);
    layers.push({ id, tools: proxy, fallback });
    changed();
    return () => { const i = layers.findIndex((l) => l.id === id); if (i >= 0) layers.splice(i, 1); changed(); };
  }, [names, fallback]);
}

const find = (name: string) => {
  for (const fb of [false, true]) for (let i = layers.length - 1; i >= 0; i--) if (layers[i].fallback === fb && layers[i].tools[name]) return layers[i].tools[name];
  return null;
};

/** Run a page tool; errors become a result the agent can read. */
export async function runPageTool(name: string, args: ToolArgs): Promise<Record<string, unknown>> {
  const t = find(name);
  if (!t) return { error: `${name} isn't available on this page` };
  try { return await t(args ?? {}); } catch (e) { return { error: e instanceof Error ? e.message : String(e) }; }
}

/** Resolves when `ok()` holds (checked on every registration change and every 100 ms), or rejects. */
export function waitFor(ok: () => boolean, ms = 15000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (ok()) { resolve(); return; }
    const done = (f: () => void) => { clearInterval(iv); clearTimeout(to); listeners.delete(check); f(); };
    const check = () => { if (ok()) done(resolve); };
    const iv = setInterval(check, 100);
    const to = setTimeout(() => done(() => reject(new Error('the page did not load in time'))), ms);
    listeners.add(check);
  });
}

// ---- Opening another stock: the app's router navigates, then the stock page reports when its chart
// is ready (so the agent's call is answered by the new page).
let navigate: ((path: string) => void) | null = null;
export const setNavigate = (f: (path: string) => void) => { navigate = f; };
const ready = { symbol: null as string | null };
/** The stock page calls this when its chart has data (null on leaving). */
export const setStockReady = (symbol: string | null) => { ready.symbol = symbol; changed(); };
export async function openStock(symbol: string) {
  const sym = symbol.trim().toUpperCase();
  if (!/^[A-Z0-9&-]{1,20}$/.test(sym)) throw new Error(`${symbol} isn't an NSE symbol`);
  if (ready.symbol === sym) return;
  ready.symbol = null;
  navigate?.(`/stock/${encodeURIComponent(sym)}${location.search}`);
  await waitFor(() => ready.symbol === sym, 20000);
}

// Development: the desk evals capture what page tools return (evals/desk/capture.ts).
if (import.meta.env.DEV) (window as unknown as { __mhqDesk: unknown }).__mhqDesk = { runPageTool };
